import { isNil } from 'lodash';
import { i18n } from '@src/i18n-setup';
import { getEnquiryRoute, getEnquiryDetailsRoute } from '@enums/route-enum';
import { isAbandonedState, isClosedState } from '@enums/state-enum';
import getState from '@utils/get-state';
import getEnquiryTitle from '@utils/enquiry/get-enquiry-title';
import { getFieldString, getFieldCurrency, getFieldDate } from '@utils/fields/common';

/**
 * Handy object to be passed to the ld-enquiry-overview component.
 *
 * @param {Object} enquiry - the api enquiry
 * @param {Object} options - { addState = false }
 * @return {Object} The processed object to use
 */
export default function getEnquiryOverviewData(enquiry, { addState = false } = {}) {
  if (isNil(enquiry)) {
    return null;
  }

  const homeLoan = enquiry.target_resources[0];
  const state = getState(enquiry);
  const hideEdit = addState || isAbandonedState(state) || isClosedState(state);

  return {
    uuid: enquiry.uuid,
    state: addState ? state : undefined,
    title: getEnquiryTitle(enquiry),
    specs: [
      enquiryStartDate(enquiry),
      {
        title: i18n.t('dashboard.overview.loan_amount'),
        value: getFieldCurrency(homeLoan.data.value),
      },
      {
        title: i18n.t('dashboard.overview.property_type'),
        value: getFieldString(`enquiry.field.loan_purpose_${homeLoan.data.loan_purpose}`, homeLoan.data.loan_purpose),
      },
      {
        title: i18n.t('dashboard.overview.loan_type'),
        value: getFieldString(`common.home_loan.loan_type.${homeLoan.data.loan_type}`, homeLoan.data.loan_type),
      },
    ],
    headerAction: hideEdit ? undefined : { svg: 'edit', to: getEnquiryDetailsRoute(enquiry.uuid, { edit: 'yes' }) },
    footerAction: {
      title: i18n.t('dashboard.overview.action.view_details'),
      to: addState ? getEnquiryRoute(enquiry.uuid) : getEnquiryDetailsRoute(enquiry.uuid),
    },
  };
}

/**
 * Returns a title/value object with the enquiry start date. This may vary on
 * the state of the enquiry.
 *
 * @param {Object} enquiry - The enquiry to get the date from.
 * @return {Object} - The detail object {title, value}
 */
function enquiryStartDate(enquiry) {
  if (enquiry.activation_at) {
    return {
      title: i18n.t('dashboard.overview.start_date'),
      value: getFieldDate(enquiry.activation_at),
    };
  }

  return {
    title: i18n.t('dashboard.overview.creation_date'),
    value: getFieldDate(enquiry.created_at),
  };
}
